import { Controller } from "@hotwired/stimulus"

// Auto-read for the Activity feed: unread rows are watched with an
// IntersectionObserver; after a short dwell in the viewport they're queued
// and persisted via one debounced batch POST. The response counts drive the
// header bell badge, More-menu badge, subtitle and "Mark all as read"
// visibility live. Attention state (needs_attention) is never touched here —
// resolving an item is what clears it.
const DWELL_MS = 2000
const FLUSH_MS = 800
const FALLBACK_MS = 5000

export default class extends Controller {
    static targets = ["item", "subtitle", "markAll"]
    static values = { url: String }

    connect() {
        this.pending = new Set()
        this.dwellTimers = new Map()
        this.observer = ("IntersectionObserver" in window) ? new IntersectionObserver((entries) => this._onIntersect(entries), { threshold: 0.5 }) : null
        // No IntersectionObserver: mark whatever rendered after a grace period.
        if (!this.observer) this.fallbackTimer = setTimeout(() => { this.itemTargets.forEach((el) => this._queue(el)) }, FALLBACK_MS)
    }

    disconnect() {
        if (this.observer) this.observer.disconnect()
        if (this.fallbackTimer) clearTimeout(this.fallbackTimer)
        if (this.flushTimer) clearTimeout(this.flushTimer)
        this.dwellTimers.forEach((timer) => clearTimeout(timer))
        this.dwellTimers.clear()
    }

    itemTargetConnected(el) {
        if (this.observer) this.observer.observe(el)
    }

    itemTargetDisconnected(el) {
        if (this.observer) this.observer.unobserve(el)
        const timer = this.dwellTimers.get(el)
        if (timer) clearTimeout(timer)
        this.dwellTimers.delete(el)
    }

    // Swipe-to-mark-read (activity-swipe controller): instant, no dwell.
    // The row updates optimistically; persistence goes through the same
    // batched flush as auto-read, so badges/subtitle refresh from the server.
    readNow(e) {
        const el = e.detail?.row
        if (!el) return
        const timer = this.dwellTimers.get(el)
        if (timer) clearTimeout(timer)
        this.dwellTimers.delete(el)
        if (this.observer) this.observer.unobserve(el)
        const id = parseInt(el.dataset.activityId || "0", 10)
        if (id > 0) {
            this.pending.add(id)
            this._markRowRead(id)
        }
        if (this.flushTimer) clearTimeout(this.flushTimer)
        this._flush()
    }

    _onIntersect(entries) {
        entries.forEach((entry) => {
            const el = entry.target
            if (entry.isIntersecting) {
                if (!this.dwellTimers.has(el)) this.dwellTimers.set(el, setTimeout(() => this._queue(el), DWELL_MS))
            } else {
                const timer = this.dwellTimers.get(el)
                if (timer) clearTimeout(timer)
                this.dwellTimers.delete(el)
            }
        })
    }

    _queue(el) {
        this.dwellTimers.delete(el)
        if (this.observer) this.observer.unobserve(el)
        const id = parseInt(el.dataset.activityId || "0", 10)
        if (id > 0) this.pending.add(id)
        if (this.flushTimer) clearTimeout(this.flushTimer)
        this.flushTimer = setTimeout(() => this._flush(), FLUSH_MS)
    }

    async _flush() {
        this.flushTimer = null
        const ids = Array.from(this.pending)
        if (!ids.length || !this.hasUrlValue) return
        this.pending.clear()
        try {
            const response = await fetch(this.urlValue, {
                method: "POST",
                headers: this._headers(),
                body: JSON.stringify({ ids: ids })
            })
            if (!response.ok) return
            const counts = await response.json()
            ids.forEach((id) => this._markRowRead(id))
            this._updateCounts(counts)
        } catch (e) {
            // Network hiccup: the rows stay unread and get retried on the next
            // page load — never break the feed over read-state bookkeeping.
        }
    }

    _markRowRead(id) {
        const row = document.getElementById(`activity_item_${id}`)
        if (!row) return
        row.classList.remove("is-unread")
        const readButton = row.querySelector(".activity-read-btn")
        const form = readButton ? readButton.closest("form") : null
        if (form) {
            form.style.transition = "opacity 0.3s ease"
            form.style.opacity = "0"
            setTimeout(() => form.remove(), 320)
        }
    }

    _updateCounts(counts) {
        const unread = parseInt(counts.unread_count, 10) || 0
        const attention = parseInt(counts.attention_count, 10) || 0
        this._updateSubtitle(unread, attention)
        this._updateBadges(unread)
        if (this.hasMarkAllTarget && unread === 0) this.markAllTarget.hidden = true
    }

    _updateSubtitle(unread, attention) {
        if (!this.hasSubtitleTarget) return
        const parts = []
        if (unread > 0) parts.push(`${unread} ${unread === 1 ? "update" : "updates"}`)
        if (attention > 0) parts.push(`${attention} ${attention === 1 ? "needs" : "need"} your attention`)
        this.subtitleTarget.textContent = parts.length ? parts.join(" · ") : "You're all caught up"
    }

    _updateBadges(unread) {
        const label = unread > 99 ? "99+" : String(unread)
        // The header bell's dot is the sitting's alone (cards waiting) since
        // 2026-10-15, so reading the feed no longer touches it.
        // Only the More menu's Activity badge — .assistant-bottom-badge--inline
        // alone also matched the Needs-you badge sitting right above it, so
        // reading activity used to overwrite the waiting count with this one.
        document.querySelectorAll(".assistant-bottom-badge--activity").forEach((badge) => {
            if (unread === 0) badge.remove()
            else badge.textContent = label
        })
    }

    _headers() {
        const token = document.querySelector("meta[name='csrf-token']")
        return { "Content-Type": "application/json", "X-CSRF-Token": token ? token.content : "" }
    }
}
